import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import { SubPageHeader } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import {
    formatReferenceMonth,
    RARO_INVEST_NO_EXPERIENCE_MESSAGE,
    listRaroInvestMonths,
    raroInvestWindow,
    referenceMonthOf,
    type DpRequest,
    type RaroInvestRequest,
} from '../../../../../shared/dpRequests';
import { RaroInvestForm } from '../../components/RaroInvestForm';
import { StatusPill } from '../../components/StatusPill';
import { describeRequestStatus } from '../../dpRequests.definitions';
import { useMyDpRequestsQuery, useRequestContextQuery } from '../../hooks/data/useDpRequestQueries';
import { DP_REQUESTS_PATH } from '../../routes';

const COUNTING_STATUSES = ['pending_dp', 'approved', 'reimbursed'];

function countingRequests(requests: DpRequest[] | undefined): RaroInvestRequest[] {
    return (requests ?? []).filter(
        (request): request is RaroInvestRequest => request.type === 'raro_invest' && COUNTING_STATUSES.includes(request.status)
    );
}

function Notice({ text }: { text: string }) {
    return (
        <p className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">{text}</p>
    );
}

export function RaroInvestPage() {
    const { user } = useAuth();
    const mineQuery = useMyDpRequestsQuery(user?.email ?? null);
    const contextQuery = useRequestContextQuery(Boolean(user?.email));
    const todayIso = contextQuery.data?.todayIso ?? '';
    const currentMonth = referenceMonthOf(todayIso || '1970-01-01');

    const counting = useMemo(() => countingRequests(mineQuery.data), [mineQuery.data]);
    const input = useMemo(
        () => ({
            experienceEndMonth: contextQuery.data?.experienceEndMonth ?? null,
            countingMonths: counting.map(request => request.referenceMonth),
            currentMonth,
        }),
        [contextQuery.data, counting, currentMonth]
    );
    const months = useMemo(() => listRaroInvestMonths(input), [input]);
    const nextMonth = useMemo(() => raroInvestWindow(input)?.minMonth ?? null, [input]);
    const loading = mineQuery.isPending || contextQuery.isPending;
    const latest = counting.slice().sort((a, b) => b.referenceMonth.localeCompare(a.referenceMonth))[0];

    return (
        <div className="mx-auto max-w-3xl space-y-6">
            <SubPageHeader
                backPlacement="end"
                backTo={DP_REQUESTS_PATH}
                icon={<AcademicCapIcon className="h-6 w-6" aria-hidden="true" />}
                title="Raro Invest"
                description="Peça o reembolso de R$ 100,00 por mês, enviando a nota de negociação de títulos."
            />

            {loading && <div className="h-64 animate-pulse rounded-2xl bg-raro-gray-100" />}
            {contextQuery.isError && (
                <p className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                    {contextQuery.error instanceof Error ? contextQuery.error.message : 'Não foi possível carregar seu cadastro.'}
                </p>
            )}

            {!loading && contextQuery.data && !contextQuery.data.isClt && (
                <Notice text="O Raro Invest é exclusivo para colaboradores CLT." />
            )}

            {!loading && contextQuery.data?.isClt && !contextQuery.data.experienceEndMonth && (
                <Notice text={RARO_INVEST_NO_EXPERIENCE_MESSAGE} />
            )}

            {!loading && contextQuery.data?.isClt && contextQuery.data.experienceEndMonth && months.length === 0 && (
                <div className="space-y-3 rounded-2xl border border-raro-gray-200 bg-white px-5 py-5 shadow-sm">
                    {latest && (
                        <div className="flex flex-wrap items-center gap-2">
                            <StatusPill label={describeRequestStatus(latest).label} tone={describeRequestStatus(latest).tone} />
                            <span className="text-sm text-raro-gray-600">Último pedido: {formatReferenceMonth(latest.referenceMonth)}</span>
                        </div>
                    )}
                    <p className="text-sm text-raro-gray-700">
                        Não há mês disponível para novo pedido{nextMonth ? `. O próximo abre em ${formatReferenceMonth(nextMonth)}` : ''}. Acompanhe o andamento em{' '}
                        <Link to={DP_REQUESTS_PATH} className="font-semibold text-primary-700 hover:underline">Meus pedidos</Link>.
                        Em caso de dúvida, fale com o DP.
                    </p>
                </div>
            )}

            {!loading && contextQuery.data?.isClt && contextQuery.data.experienceEndMonth && months.length > 0 && (
                <RaroInvestForm months={months} />
            )}
        </div>
    );
}

export default RaroInvestPage;
